/**
 * One funding request, from the customer's side.
 *
 * The order on the page is the order things happened: the request, the routing
 * run that chose who saw it, the applications that resulted, the offers, and
 * the eligibility evaluations behind each one. That order is what makes the
 * question "why did this provider see my data" answerable by scrolling rather
 * than by asking.
 *
 * Routing is an action here rather than something that happens on raising a
 * request, because it discloses data to providers and that disclosure needs a
 * consent decision taken knowingly.
 */
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { api } from '../../api/client'
import type { Dict, SectionMeta } from '../../api/types'
import { useSession } from '../../app/session'
import { appPath } from '../../app/paths'
import { Badge, Empty, KV, Note, toneOf } from '../../components/bits'
import { PageHead } from '../../components/PageHead'
import { Panel, Cols } from '../../components/Panel'

export function RequestRecord({ data }: { data: Dict }) {
  const { n, amount, d, dt, lbl, can, boot } = useSession()
  const navigate = useNavigate()
  const fr = data.fr as Dict
  const section = data.section as SectionMeta
  const apps = (data.apps || []) as Dict[]
  const offers = (data.offers || []) as Dict[]
  const run = data.run as Dict | null
  const evals = (data.evals || []) as Dict[]

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [consented, setConsented] = useState(false)

  const route = async () => {
    setBusy(true)
    setError(null)
    try {
      const result = await api.act(`/app/requests/${fr.request_id}/route`,
                                   { consumer_consent: consented ? 'on' : '' })
      const url = new URL(result.redirect, window.location.origin)
      const refused = url.searchParams.get('error')
      if (refused) { setError(refused); return }
      navigate(0)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHead section={section} title={fr.request_ref as string}
                auditLink={data.audit_link as string}
                actions={offers.length > 1
                  ? [{ label: 'Compare offers', style: 'primary',
                       href: `/app/requests/${fr.request_id}/compare` }]
                  : []} />

      {error && <Note kind="bad"><b>Not done.</b> {error}</Note>}

      <Cols>
        <Panel title="The request" explain="funding.my_requests">
          <div className="big-value">
            <b className="num">{amount(fr.amount_requested, fr.currency as string)}</b>
            <span className="muted">{lbl(fr.economic_purpose)}</span>
          </div>
          <KV rows={[
            ['Reference', <code key="r">{fr.request_ref as string}</code>],
            ['State', <Badge tone={toneOf(fr.status)} key="s">{lbl(fr.status)}</Badge>],
            ['Tenor', fr.tenor_months ? `${n(fr.tenor_months)} months` : 'unstated'],
            ['Compliance', fr.compliance_required
              ? <Badge tone={toneOf(fr.compliance_required)} key="c">
                  {lbl(fr.compliance_required)}
                </Badge>
              : 'either, so both are routed'],
            ['Urgency', fr.urgency_days
              ? `${n(fr.urgency_days)} days` : 'unstated'],
            ['Raised', dt(fr.created_at)],
            ...(fr.closed_at ? [['Closed', dt(fr.closed_at)] as [string, string]] : []),
            ...(fr.purpose_text
              ? [['In your words', String(fr.purpose_text)] as [string, string]]
              : []),
          ]} />
        </Panel>

        <Panel title="Who saw it, and why" explain="routing.runs">
          {run ? (
            <>
              <KV rows={[
                ['Run', dt(run.run_at)],
                ['Considered', `${n(run.candidates_considered)} products`],
                ['Routed to', `${n(run.candidates_routed)} providers`],
                ['Algorithm', <code key="a">{run.algorithm_version as string}</code>],
                ['Took', `${n(run.duration_ms)} ms`],
              ]} />
              <Link className="btn sm" to={appPath(`/app/routing/${run.run_id}`)}>
                Open the run and its ranking
              </Link>
              <p className="xs muted">
                Four filters in a fixed order, then a ranking on four published
                weights: approval fit {n(boot.constants.weights.approval_fit * 100)}%,
                speed {n(boot.constants.weights.speed * 100)}%, product fit{' '}
                {n(boot.constants.weights.product_fit * 100)}% and your stated
                preference {n(boot.constants.weights.preference * 100)}%. Fee
                level is absent from the weights by design.
              </p>
            </>
          ) : (
            <>
              <Empty title="Nothing has been routed yet">
                No provider has seen anything about you in connection with this
                request.
              </Empty>
              {can('funding.request_new', 'create') && (
                <div className="inline-form">
                  <label className="check">
                    <input type="checkbox" checked={consented}
                           onChange={(e) => setConsented(e.target.checked)} />
                    I consent to the providers chosen by this run reading the
                    attributes listed in my consent grant, for the purpose of
                    assessing this request.
                  </label>
                  <button className="btn primary" type="button"
                          disabled={busy || !consented}
                          onClick={() => void route()}>
                    {busy ? 'Routing…' : 'Route this request'}
                  </button>
                  <p className="xs muted">
                    Routing discloses data. The consent gateway is asked for each
                    provider separately and fails closed, so a provider the
                    gateway refuses is not routed to rather than routed to with
                    an empty pack.
                  </p>
                </div>
              )}
            </>
          )}
        </Panel>
      </Cols>

      <Panel title={`Applications · ${apps.length}`} explain="application.queue" tight>
        {apps.length === 0 ? (
          <Empty title="No application exists yet" />
        ) : (
          <div className="tbl-wrap">
            <table className="dl">
              <thead>
                <tr>
                  <th>Reference</th><th>Provider</th><th>Product</th>
                  <th>State</th><th>Decision</th><th>Reason given</th>
                  <th>Routed</th><th className="acts" />
                </tr>
              </thead>
              <tbody>
                {apps.map((a) => (
                  <tr key={String(a.application_id)}>
                    <td data-label="Reference">
                      <span className="mono clip">{a.application_ref as string}</span>
                    </td>
                    <td data-label="Provider">{a.provider_name as string}</td>
                    <td data-label="Product">{(a.product_name as string) || '—'}</td>
                    <td data-label="State">
                      <Badge tone={toneOf(a.status)}>{lbl(a.status)}</Badge>
                    </td>
                    <td data-label="Decision">
                      {a.decision
                        ? <Badge tone={toneOf(a.decision)}>{lbl(a.decision)}</Badge>
                        : <span className="muted">pending</span>}
                    </td>
                    <td data-label="Reason given">
                      {(a.decline_reason_text as string)
                        || (a.decline_reason_code
                          ? lbl(a.decline_reason_code) : '—')}
                    </td>
                    <td data-label="Routed">{a.routed_at ? d(a.routed_at) : '—'}</td>
                    <td className="acts">
                      <Link className="btn sm"
                            to={appPath(`/app/applications/${a.application_id}`)}>
                        Open
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="xs muted">
          A decline carries a reason from a published code list, and a code that
          would reveal a provider's criteria is not shown to you. The distinction
          is recorded on the code rather than decided per decline.
        </p>
      </Panel>

      <Panel title={`Offers · ${offers.length}`} explain="offer.list" tight>
        {offers.length === 0 ? (
          <Empty title="No offer yet" />
        ) : (
          <div className="tbl-wrap">
            <table className="dl">
              <thead>
                <tr>
                  <th>Provider</th><th className="num">Amount</th>
                  <th className="num">Tenor</th>
                  <th className="num">Total cost of finance</th>
                  <th>State</th><th>Expires</th>
                </tr>
              </thead>
              <tbody>
                {offers.map((o) => (
                  <tr key={String(o.offer_id)}>
                    <td data-label="Provider">{o.provider_name as string}</td>
                    <td data-label="Amount" className="num">
                      {amount(o.amount_offered, o.currency_code as string)}
                    </td>
                    <td data-label="Tenor" className="num">{n(o.tenor_months)} mo</td>
                    <td data-label="Total cost of finance" className="num">
                      {amount(o.total_cost_of_finance, o.currency_code as string)}
                    </td>
                    <td data-label="State">
                      <Badge tone={toneOf(o.status)}>{lbl(o.status)}</Badge>
                    </td>
                    <td data-label="Expires">{o.expires_at ? d(o.expires_at) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {evals.length > 0 && (
        <Panel title={`Eligibility evaluations · ${evals.length}`}
               explain="rules.evaluations" tight>
          <div className="tbl-wrap">
            <table className="dl">
              <thead>
                <tr>
                  <th>Rule set</th><th>Outcome</th><th>Failed</th>
                  <th>Indeterminate</th><th>Evaluated</th><th className="acts" />
                </tr>
              </thead>
              <tbody>
                {evals.map((e) => (
                  <tr key={String(e.evaluation_id)}>
                    <td data-label="Rule set">
                      <code>{e.ruleset_id as string} v{e.ruleset_version as string}</code>
                    </td>
                    <td data-label="Outcome">
                      <Badge tone={toneOf(e.outcome)}>{lbl(e.outcome)}</Badge>
                    </td>
                    <td data-label="Failed">
                      {((e.failed_rules || []) as string[]).join(', ') || '—'}
                    </td>
                    <td data-label="Indeterminate">
                      {((e.indeterminate_rules || []) as string[]).join(', ') || '—'}
                    </td>
                    <td data-label="Evaluated">{dt(e.evaluated_at)}</td>
                    <td className="acts">
                      <Link className="btn sm"
                            to={appPath(`/app/evaluations/${e.evaluation_id}`)}>
                        Open
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="xs muted">
            An indeterminate rule is one the data could not answer, and it is not
            a failure. Treating missing data as a failure would make consent
            coercive: a business that declined to share one attribute would be
            declined rather than assessed on what it did share.
          </p>
        </Panel>
      )}
    </>
  )
}
